import { useState } from 'react';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface FadeIdProps {
    id: string;
    className?: string;
    onCopy?: () => void;
    /** Width of the ID text container. Default: '140px' */
    idWidth?: string;
    /** Height of the component. Default: 'h-8' */
    height?: string;
    /** Font size of the ID text. Default: 'text-xs' */
    fontSize?: string;
    /** Size of the copy button icon. Default: 'md' (h-5 w-5 container, h-3.5 w-3.5 icon) */
    iconSize?: 'sm' | 'md' | 'lg';
    /** Label for what is being copied (e.g., "Assistant ID", "User ID"). Default: "ID" */
    label?: string;
    /** Tooltip position. Default: 'top' */
    tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
    /** Mode: 'copy' for copy button, 'link' for external link button. Default: 'copy' */
    mode?: 'copy' | 'link';
    /** URL to open when mode is 'link'. Opens in new tab. */
    linkUrl?: string;
}

export function FadeId({ 
    id, 
    className, 
    onCopy,
    idWidth = '140px',
    height = 'h-8',
    fontSize = 'text-xs',
    iconSize = 'md',
    label = 'ID',
    tooltipSide = 'top',
    mode = 'copy',
    linkUrl
}: FadeIdProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
            onCopy?.();
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleLink = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (linkUrl) {
            window.open(linkUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Icon size configurations
    const iconSizes = {
        sm: { container: 'h-4 w-4', icon: 'h-2.5 w-2.5' },
        md: { container: 'h-5 w-5', icon: 'h-3 w-3' },
        lg: { container: 'h-6 w-6', icon: 'h-3.5 w-3.5' }
    };

    const currentIconSize = iconSizes[iconSize];

    return (
        <button
            onClick={mode === 'link' ? handleLink : handleCopy}
            className={cn(
                "group relative inline-flex items-center gap-1.5 pl-3 pr-2 rounded-md",
                "border border-border/80",
                "bg-muted/40 hover:bg-muted/60 transition-all duration-200",
                "font-mono text-foreground/70",
                "shadow-sm hover:shadow-sm",
                height,
                fontSize,
                className
            )}
        >
            {/* ID Text Container with mask-based fade */}
            <div className="relative overflow-hidden" style={{ width: idWidth }}>
                <span 
                    className="block whitespace-nowrap"
                    style={{
                        maskImage: 'linear-gradient(to right, black 0%, black 40%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to right, black 0%, black 40%, transparent 100%)'
                    }}
                >
                    {id}
                </span>
            </div>
            
            {/* Action Icon - Copy or Link */}
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className={cn(
                            "flex items-center justify-center flex-shrink-0 rounded-sm bg-background border border-border/70 cursor-pointer",
                            currentIconSize.container
                        )}>
                            {mode === 'link' ? (
                                <ExternalLink className={cn("text-foreground/60 group-hover:text-foreground transition-colors", currentIconSize.icon)} />
                            ) : copied ? (
                                <Check className={cn("text-green-600 dark:text-green-500", currentIconSize.icon)} />
                            ) : (
                                <Copy className={cn("text-foreground/60 group-hover:text-foreground transition-colors", currentIconSize.icon)} />
                            )}
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side={tooltipSide} className="font-sans">
                        <p className="text-xs font-normal">
                            {mode === 'link' 
                                ? `Open ${label}` 
                                : copied 
                                    ? 'Copied!' 
                                    : `Click to copy ${label}`
                            }
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </button>
    );
}

